/**
 * mSTEM — AI panel control → mock result mapper (Phase 7 / AI-01, Plan 07-03).
 *
 * The single bridge between an AI-panel control-card button (controlId) and the
 * SAME mock-truth source the VM block opcodes read (src/lib/mstem-mock/ai-results.js).
 * The panel does NOT invoke the VM (RESEARCH Open Q2 — single source of mock truth);
 * it calls getPanelResult(controlId) and renders the returned {kind, payload} inline.
 *
 * MOCK-ONLY (DR-00): values come straight from ai-results.js; no inference, no cloud,
 * no network. Mirrors the block opcode outputs so the standalone panel demo and the
 * workspace report bubbles always agree.
 *
 * Result shape: {kind, payload}
 *   - kind 'text'   → payload is a string/number rendered as plain Body text
 *   - kind 'badges' → payload is a string[] rendered as MstemBadge pills
 *   - kind 'table'  → payload is {columns, rows} rendered via MstemTable
 *
 * ES module (GUI-side; imported by the React container). ai-results.js is CommonJS
 * (module.exports) but Babel interop lets us default-import it here.
 */
import aiResults from './ai-results';

const pct = conf => `${Math.round(conf * 100)}%`;

/**
 * Map an AI-panel control id to a renderable mock result.
 *
 * @param {string} controlId one of the gen.ai.panel.btn.* control ids
 * @returns {{kind: string, payload: *}} the renderable mock result
 */
const getPanelResult = controlId => {
    switch (controlId) {
    // ── Khuôn mặt ──────────────────────────────────────────────
    case 'face.count':
        return {kind: 'text', payload: aiResults.faces.length};
    case 'face.identify':
        return {
            kind: 'badges',
            payload: aiResults.faces.map(f => `${f.name} (${pct(f.confidence)})`)
        };
    case 'face.analyzeSave':
        return {
            kind: 'table',
            payload: {
                columns: [
                    {key: 'name', label: 'Khuôn mặt'},
                    {key: 'pos', label: 'x, y'},
                    {key: 'conf', label: 'Độ tin cậy'}
                ],
                rows: aiResults.faces.map((f, idx) => ({
                    id: idx,
                    name: f.name,
                    pos: `${f.x}, ${f.y}`,
                    conf: pct(f.confidence)
                }))
            }
        };

    // ── Cơ thể ─────────────────────────────────────────────────
    case 'body.objectCount':
        return {kind: 'text', payload: aiResults.objectCount};
    case 'body.partXY':
        return {
            kind: 'table',
            payload: {
                columns: [
                    {key: 'part', label: 'Bộ phận'},
                    {key: 'x', label: 'x'},
                    {key: 'y', label: 'y'}
                ],
                rows: Object.keys(aiResults.bodyParts).map((part, idx) => ({
                    id: idx,
                    part,
                    x: aiResults.bodyParts[part].x,
                    y: aiResults.bodyParts[part].y
                }))
            }
        };
    case 'body.analyzeHand':
        return {
            kind: 'text',
            payload: `${aiResults.handData.fingers} ngón — ${aiResults.handData.gesture}`
        };

    // ── Hình ảnh ───────────────────────────────────────────────
    case 'image.captureIdentify':
        return {kind: 'badges', payload: aiResults.imageLabels.label};
    case 'image.objectCount':
        return {kind: 'text', payload: aiResults.objectCount};
    case 'image.brand':
        return {kind: 'badges', payload: aiResults.imageLabels.brand};

    // ── OCR ────────────────────────────────────────────────────
    case 'ocr.fromCamera':
        return {kind: 'text', payload: aiResults.ocrText};

    default:
        return {kind: 'text', payload: ''};
    }
};

export {
    getPanelResult as default,
    getPanelResult
};
